import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { updateConfig } from "../../../../../store/slices/User/SubscriptionPlans/subscriptionPlanSlice";
import {
  useGetSubscriptionPlanBasicDataQuery,
  useGetSubscriptionPlanPreviewMutation,
  useGetSubscriptionPlanProductsQuery,
  useUploadSubscriptionPlanImageMutation,
} from "../../../../../store/queries/Subscription";
import { createAndUpdateSubscriptionPlan } from "../../api";

const useAddSubscriptionPlan = (handleCreateClick, refetch) => {
  const dispatch = useDispatch();
  const [isPlanSaving, setIsPlanSaving] = useState(false);

  const {
    isEdit,
    editPlanData,
    imageName,
    imagePreview,
    aliasImageName,
    aliasImagePreview,
    isImageUploading,
    isAliasImageUploading,
    pricing,
    isPricingLoading,
  } = useSelector((state) => state.subscriptionPlan);

  const { data: basicData = {} } = useGetSubscriptionPlanBasicDataQuery();
  const [uploadPlanImage] = useUploadSubscriptionPlanImageMutation();
  const [getPlanPreview] = useGetSubscriptionPlanPreviewMutation();

  // Memoised because the ?? [] fallback would otherwise hand a fresh array to the pickers
  // on every render, which defeats the lookups built from them further down.
  const planStatuses = useMemo(
    () => basicData?.data?.plan_statuses ?? [],
    [basicData]
  );
  const planAudiences = useMemo(
    () => basicData?.data?.plan_audiences ?? [],
    [basicData]
  );
  const storeList = useMemo(() => basicData?.data?.store ?? [], [basicData]);

  /**
   * Yup casts "" to NaN rather than to empty, so an untouched number field fails with a type
   * error instead of being treated as absent. Every number here is fed through this: without
   * it the *unused* half of the discount/price pair alone would block submission.
   */
  const emptyToNull = (value, original) => (original === "" ? null : value);

  const validation = Yup.object({
    // The team's, always. Several sizes at once each take this name with their own size
    // appended, so one box still covers the lot.
    name: Yup.string().trim().required("Enter plan name").max(100),
    /**
     * The app is bilingual, and nothing falls back to the English copy - a plan saved without
     * the Arabic half renders blank to half the customers. Required here rather than in the
     * service, which still accepts null so that plans authored before this rule stay saveable
     * by anything other than this form.
     */
    alias_name: Yup.string().trim().required("Enter the Arabic plan name").max(100),
    description: Yup.string().trim().required("Enter a description").max(1000),
    alias_description: Yup.string()
      .trim()
      .required("Enter the Arabic description")
      .max(1000),
    terms: Yup.string().trim().required("Enter the terms").max(5000),
    alias_terms: Yup.string().trim().required("Enter the Arabic terms").max(5000),
    /**
     * Holds the uploaded artwork's URL, not the file itself. The plan tile has nothing to
     * fall back to, so a plan without one ships a blank card. The Arabic image stays optional
     * - artwork without text on it serves both.
     */
    image: Yup.string().required("Add a plan image"),
    /**
     * The app's discount badge. Short on purpose - it renders in a corner of the plan tile.
     * Required in both languages: the app has no fallback in either direction, so a plan
     * saved without one ships a bare tile. Plans authored before this rule hold none and go
     * on rendering without a badge, which is why the service still takes these as nullable.
     */
    discount_text: Yup.string()
      .trim()
      .required("Enter the discount text")
      .max(50, "At most 50 characters"),
    alias_discount_text: Yup.string()
      .trim()
      .required("Enter the Arabic discount text")
      .max(50, "At most 50 characters"),
    /**
     * The service takes this as nullable, since plans authored before the outlet was recorded
     * hold none. Required here so that no *new* plan is saved without its provenance - which
     * also means editing one of those older plans now has to name an outlet before it will
     * save, and naming one clears the beverage.
     */
    menu_customer_key: Yup.string().required("Select an outlet"),
    // The sizes are what the team actually picks, and the product is read off them at save -
    // so this is the only rule the item needs, and the message belongs on the field that has
    // one rather than on a hidden one nobody can act on.
    selected_options: Yup.array().min(1, "Choose at least one size"),
    quantity: Yup.number()
      .transform(emptyToNull)
      .required("Enter the number of items")
      .integer("Enter a whole number")
      .min(1, "At least 1 item")
      .max(100, "At most 100 items"),
    // Either of the two prices is enough - the service derives whichever is missing - but
    // one of them has to be there, so the requirement follows the chosen mode.
    // Required only while the plan is one size. Several sizes are priced one by one below,
    // and this field is not shown, so requiring it would block a save on a box nobody can see.
    discount_percent: Yup.number()
      .transform(emptyToNull)
      .nullable()
      .when(["pricing_mode", "selected_options"], {
        is: (mode, chosen) => mode === "discount" && (chosen ?? []).length < 2,
        then: (schema) =>
          schema
            .required("Enter a discount")
            .min(0, "Discount cannot be negative")
            .lessThan(100, "Discount must be under 100%"),
      }),
    /**
     * A discount per size, keyed by variant id. Checked by hand rather than by shape, because
     * the keys are the sizes the team ticked and so are not known to the schema.
     */
    discount_percents: Yup.object().when(["pricing_mode", "selected_options"], {
      is: (mode, chosen) => mode === "discount" && (chosen ?? []).length > 1,
      then: (schema) =>
        schema.test(
          "every-size-priced",
          "Enter a discount under 100% for every size",
          (value) => {
            const entries = Object.values(value ?? {});

            return (
              entries.length > 0 &&
              entries.every(
                (entry) =>
                  entry !== "" &&
                  entry !== null &&
                  entry !== undefined &&
                  Number(entry) >= 0 &&
                  Number(entry) < 100
              )
            );
          }
        ),
    }),
    // Required only while the plan is one size, for the same reason the discount above is:
    // several sizes are priced one by one below and this field is not shown.
    selling_price: Yup.number()
      .transform(emptyToNull)
      .nullable()
      .when(["pricing_mode", "selected_options"], {
        is: (mode, chosen) => mode === "price" && (chosen ?? []).length < 2,
        then: (schema) =>
          schema
            .required("Enter a selling price")
            .moreThan(0, "Selling price must be more than zero")
            .test(
              "three-decimals",
              "At most three decimals (fils)",
              (value) =>
                value === undefined ||
                value === null ||
                /^\d+(\.\d{1,3})?$/.test(String(value))
            ),
      }),
    /** A price per size, checked by hand for the same reason the discounts above are. */
    selling_prices: Yup.object().when(["pricing_mode", "selected_options"], {
      is: (mode, chosen) => mode === "price" && (chosen ?? []).length > 1,
      then: (schema) =>
        schema.test(
          "every-size-priced",
          "Enter a price above zero, to at most three decimals, for every size",
          (value) => {
            const entries = Object.values(value ?? {});

            return (
              entries.length > 0 &&
              entries.every(
                (entry) =>
                  entry !== "" &&
                  entry !== null &&
                  entry !== undefined &&
                  Number(entry) > 0 &&
                  /^\d+(\.\d{1,3})?$/.test(String(entry))
              )
            );
          }
        ),
    }),
    validity_days: Yup.number()
      .transform(emptyToNull)
      .required("Enter the validity")
      .integer("Enter a whole number")
      .min(1, "At least 1 day")
      .max(730, "At most 730 days"),
    /**
     * Daily Limit throttles a pool; it does not grant a daily allowance. A limit above the
     * number of items can never bind - at redemption the per-line balance check refuses
     * first and the day cap is never consulted - so the field would describe a cap the plan
     * can never reach. Mirrors the rule the service applies in SubscriptionPlanCreateRequest,
     * so the form refuses what the API would refuse rather than inviting a 422.
     *
     * Equality passes: a limit equal to the item count is inert too, but it is what the form
     * opens on, and it reads as the honest "no throttle" rather than a mistake. Zero passes
     * for the same reason - it is the service's "unconfigured".
     */
    daily_limit: Yup.number()
      .transform(emptyToNull)
      .required("Enter the daily limit")
      .integer("Enter a whole number")
      .min(0, "Cannot be negative")
      .max(100, "At most 100 per day")
      .test("within-item-count", "", function (value) {
        const quantity = Number(this.parent.quantity);

        if (!Number.isInteger(quantity) || quantity < 1) return true;
        if (!Number.isInteger(value) || value < 1) return true;
        if (value <= quantity) return true;

        return this.createError({
          message:
            quantity === 1
              ? `A one-item plan is a single redemption, so a limit of ${value} a day can never be reached. Set it to 1.`
              : `This plan holds ${quantity} items, so a limit of ${value} a day can never be reached. Set it to ${quantity} or below.`,
        });
      }),
    purchase_limit: Yup.number()
      .transform(emptyToNull)
      .nullable()
      .integer("Enter a whole number")
      .min(0, "Cannot be negative"),
    stores: Yup.array().when("all_stores", {
      is: false,
      then: (schema) => schema.min(1, "Choose at least one outlet"),
    }),
    status: Yup.number().required("Select a status"),
    audience: Yup.number().required("Select an audience"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      alias_name: "",
      description: "",
      alias_description: "",
      terms: "",
      alias_terms: "",
      discount_text: "",
      alias_discount_text: "",
      image: "",
      alias_image: "",

      /**
       * Sources the beverage picker only - it is never sent. Menus are held per outlet, so
       * the team has to say which outlet's menu they are choosing from before there is a
       * list to choose from. Which outlets the plan is *sold* at is `all_stores`/`stores`.
       */
      menu_customer_key: "",

      /**
       * The drink itself - "Latte" rather than "Latte - Small". Filters the size picker below
       * and nothing else: the plan is defined by the sizes chosen there, not by this.
       * Empty means the size picker is unfiltered, which is how a product with no sizes at all
       * is still reachable.
       */
      main_product_id: "",

      /**
       * The sizes chosen, as the catalogue's option ids. One each becomes a plan: Small,
       * Medium and Large ticked together mint three, sharing everything on this form and
       * differing only in the size they name and the price that follows from it.
       *
       * An edit holds exactly one - it addresses a single plan, and repricing a size must not
       * reach across to its siblings.
       */
      selected_options: [],

      quantity: 1,

      /** "discount" or "price" - only the chosen one is sent. */
      pricing_mode: "discount",
      discount_percent: "",
      /** Both keyed by variant id, and only used once there are several sizes to tell apart. */
      discount_percents: {},
      selling_prices: {},
      selling_price: "",

      validity_days: 30,
      daily_limit: 1,
      purchase_limit: "",
      all_stores: true,
      stores: [],
      status: 1,
      /** 1 = Public. A plan is for the app unless the team says otherwise. */
      audience: 1,
      priority: 0,

      /**
       * Whether the app lists this plan under its beverage's tile with the other sizes.
       * Off unless the team says so: two plans naming one drink are not necessarily two
       * sizes of it, and a campaign swept in beside the Latte sizes is a listing nobody
       * asked for.
       */
      group_by_product: false,

      /**
       * Which of the ticked sizes the app opens the group on - the radio already selected on
       * the detail screen, and the size the listing card prices.
       *
       * Blank is a real answer and the default one: no size chosen leaves the app to lead with
       * the dearest. Only meaningful with more than one size ticked.
       */
      default_variant: "",
    },
    validationSchema: validation,
    onSubmit: async (values) => {
      setIsPlanSaving(true);

      const data = {
        name: values?.name,
        alias_name: values?.alias_name,
        description: values?.description,
        alias_description: values?.alias_description,
        terms: values?.terms,
        alias_terms: values?.alias_terms,
        // What the app prints on the plan's badge. Left blank means no badge - it is not
        // filled in from the discount, which is what the app used to show here.
        discount_text: values?.discount_text,
        alias_discount_text: values?.alias_discount_text,
        image: values?.image,
        alias_image: values?.alias_image,
        // Exactly one line, and it must be a product. A category line covers every product
        // frozen into it, which is many products in a plan by another name.
        lines: [
          {
            type: "product",
            // Read off the ticked sizes rather than held in their own fields: every size of
            // one drink shares a product_id, and when several are ticked the service is told
            // the rest through `variants` below and mints a plan each.
            product_id: selectedRows[0]?.product_id ?? "",
            variant_id: selectedRows[0]?.variant_id || null,
            quantity: Number(values?.quantity),
            // Recorded so an edit can reopen on the menu the item came from. Null on a plan
            // whose outlet was never captured, which the service accepts.
            menu_customer_key: values?.menu_customer_key || null,
          },
        ],
        validity_days: Number(values?.validity_days),
        daily_limit: Number(values?.daily_limit),
        purchase_limit:
          values?.purchase_limit === "" || values?.purchase_limit === null
            ? null
            : Number(values?.purchase_limit),
        all_stores: Boolean(values?.all_stores),
        // all_stores clears the list rather than being resolved against it - sending both
        // is refused, since a plan meant for one outlet would otherwise go on sale at every
        // outlet.
        stores: values?.all_stores ? [] : values?.stores,
        status: Number(values?.status),
        audience: Number(values?.audience),
        priority: Number(values?.priority || 0),
        // The grouping key is the product the line above already names, so this flag is the
        // whole of what the plan itself has to say about being grouped.
        group_by_product: Boolean(values?.group_by_product),
      };

      /**
       * Several sizes are priced one by one, so the map goes instead of the single figure - not
       * alongside it, which would leave two answers to what a size costs and the service picking
       * between them.
       *
       * Built from the ticked sizes rather than from the map itself, so a size unticked after it
       * was priced cannot leave its figure behind: the service refuses one naming a size the
       * plan does not include.
       */
      if (isPerSizePricing) {
        data[perSizeField] = selectedRows.reduce((carry, row) => {
          const variantId = row?.variant_id;

          if (variantId) {
            carry[variantId] = Number(values?.[perSizeField]?.[variantId] ?? 0);
          }

          return carry;
        }, {});
      } else if (values?.pricing_mode === "discount") {
        data.discount_percent = Number(values?.discount_percent);
      } else {
        data.selling_price = Number(values?.selling_price);
      }

      /**
       * The sizes to mint a plan for. Sent only when there are several - one size is the
       * ordinary single-plan save, and the service reads it off the line above.
       *
       * Never on an edit: an edit addresses one plan, and the service refuses these alongside
       * a plan_id for exactly that reason.
       */
      if (selectedRows.length > 1 || (isEdit && lockedSizes.length > 1)) {
        data.variants = selectedRows
          .map((row) => row?.variant_id)
          .filter(Boolean);

        /**
         * Sent alongside the sizes, and only ever one of them - the service refuses a default
         * naming a size the group does not contain.
         *
         * Sent even when blank, because blank is how the team unpicks a choice: omitting it
         * would leave the size they had already marked standing as the default.
         */
        data.default_variant = data.variants.includes(values?.default_variant)
          ? values.default_variant
          : null;
      }

      if (isEdit) {
        data.plan_id = editPlanData?._id;
      }

      const response = await createAndUpdateSubscriptionPlan(data);

      if (response?.data?.status_code === 200) {
        // Editing a published plan supersedes it rather than rewriting it, so the team is
        // told which version they are now looking at - customers stay on what they bought.
        const version = response?.data?.data?.version;
        toast.success(
          isEdit
            ? version
              ? `Plan updated - now version ${version}`
              : `Plan updated successfully`
            : `Plan created successfully`
        );
        setIsPlanSaving(false);
        handleCreateClick();
        refetch();
        return;
      }

      const errors = response?.errors ?? response?.data?.errors;
      if (errors) {
        Object.keys(errors).forEach((field) => {
          // lines.0.product_id and the like have no matching form field, so the message is
          // put on the control the team can actually act on - the size picker, which is where
          // the item is chosen.
          const target = field.startsWith("lines") ? "selected_options" : field;
          formik.setFieldError(
            target,
            Array.isArray(errors[field]) ? errors[field][0] : errors[field]
          );
        });
      } else {
        toast.error(
          response?.message ?? response?.data?.message ?? `Something went wrong!`
        );
      }
      setIsPlanSaving(false);
    },
  });

  /**
   * Formik marks a field touched on blur, so a message found while typing stays hidden until
   * the cursor leaves the box - on a form this long that is usually several fields later.
   * Marking touched on change surfaces it at the first keystroke instead, while still leaving
   * a freshly opened form clean, which dropping the touched check altogether would not.
   */
  const handleFieldChange = (event) => {
    formik.handleChange(event);
    const field = event?.target?.name;
    if (field) {
      // false: handleChange has already revalidated against the new value.
      formik.setFieldTouched(field, true, false);
    }
  };

  /** getFieldProps, but the message appears as the team types rather than on blur. */
  const fieldProps = (field) => ({
    ...formik.getFieldProps(field),
    onChange: handleFieldChange,
  });

  /** The pickers write their value directly, so they mark themselves touched the same way. */
  const setFieldValueTouched = (field, value) => {
    formik.setFieldValue(field, value);
    formik.setFieldTouched(field, true, false);
  };

  // Second mode of basic-data: that outlet's menu, and nothing else. Skipped until an
  // outlet is named, since there is no sensible list to show before then.
  const { data: productData = {}, isFetching: isProductsFetching } =
    useGetSubscriptionPlanProductsQuery(
      { customer_key: formik.values.menu_customer_key },
      { skip: !formik.values.menu_customer_key }
    );

  /**
   * Memoised on the list itself rather than on the query result.
   *
   * A skipped RTK query answers `undefined`, so the `= {}` default above mints a fresh object
   * on every render - keying on it made this array a new reference each time too, and anything
   * downstream that watched its identity re-ran forever.
   */
  const productList = useMemo(
    () => productData?.data?.product ?? [],
    [productData?.data?.product]
  );

  /**
   * The drinks, one row each - the picker answers per size, so "Latte" arrives three times and
   * only the first of them names the product.
   */
  const mainProductList = useMemo(() => {
    const seen = new Map();

    (productList ?? []).forEach((row) => {
      if (row?.product_id && !seen.has(row.product_id)) {
        seen.set(row.product_id, {
          product_id: row.product_id,
          // product_name is the bare drink; the older combined label is the fallback for a
          // catalog that has not been redeployed with the split names yet.
          name: row.product_name || row.name,
          image: row.image,
        });
      }
    });

    return [...seen.values()];
  }, [productList]);

  /**
   * The sizes on offer. Narrowed to the chosen drink, and every option when none is chosen -
   * which is what keeps a product with no sizes of its own reachable.
   */
  const sizeList = useMemo(() => {
    const chosen = formik.values.main_product_id;

    return chosen
      ? (productList ?? []).filter((row) => row?.product_id === chosen)
      : productList ?? [];
  }, [productList, formik.values.main_product_id]);

  /**
   * The rows behind the ticked sizes.
   *
   * Falls back to the plan's own saved line when the menu cannot answer - the plan predates the
   * outlet being recorded, or the catalog call is still in flight - so an edit opens showing the
   * size it plainly holds rather than an empty picker.
   */
  const selectedRows = useMemo(() => {
    const chosen = formik.values.selected_options ?? [];

    if (chosen.length === 0) {
      return [];
    }

    const fromMenu = (productList ?? []).filter((row) =>
      chosen.includes(row?.option_id)
    );

    if (fromMenu.length > 0) {
      return fromMenu;
    }

    const savedLine = editPlanData?.lines?.[0];
    const savedOption = savedLine?.variant_id || savedLine?.product_id;

    return savedLine && chosen.includes(savedOption)
      ? [
          {
            option_id: savedOption,
            product_id: savedLine?.product_id,
            variant_id: savedLine?.variant_id ?? null,
            name: savedLine?.name,
            product_name: savedLine?.name,
            variant_name: "",
            list_price: savedLine?.list_price,
          },
        ]
      : [];
  }, [productList, formik.values.selected_options, editPlanData]);

  /**
   * A stable identity for the ticked sizes.
   *
   * The rows themselves are rebuilt whenever the menu is, so watching them in an effect would
   * re-run it on renders where nothing was actually chosen or unchosen.
   */
  const selectedKey = selectedRows.map((row) => row?.option_id).join(",");

  /**
   * Whether the sizes are priced one by one.
   *
   * Several sizes are several plans and do not sell alike, so each carries its own figure - one
   * percentage, or one price, across them prices at least one of them wrong. A single size has
   * nothing to tell apart and keeps the one field it always had.
   *
   * Applies in both modes. The field it drives differs - a discount per size, or a price per
   * size - but the question it answers is the same one.
   */
  const isPerSizePricing = selectedRows.length > 1;

  /** Which map the chosen mode fills, and so which one is sent and seeded. */
  const perSizeField =
    formik.values.pricing_mode === "discount"
      ? "discount_percents"
      : "selling_prices";

  /**
   * The sizes the group already sells.
   *
   * They can be added to but not taken away: each has been on sale, and withdrawing one is a
   * decision to take deliberately on that plan rather than by unticking a box while editing
   * another. The picker locks them; this is what it locks.
   */
  const lockedSizes = useMemo(
    () => (editPlanData?.group_sizes ?? []).map((size) => size.variant_id),
    [editPlanData]
  );

  useEffect(() => {
    if (isEdit && editPlanData && Object.keys(editPlanData).length > 0) {
      const line = editPlanData?.lines?.[0] ?? {};
      formik?.setValues({
        // The group's name where there is one - "Latte Lovers" rather than "Latte Lovers -
        // Large" - so saving re-appends each size rather than stacking a second one on.
        name: editPlanData?.group_name ?? editPlanData?.name ?? "",
        alias_name: editPlanData?.alias_group_name ?? editPlanData?.alias_name ?? "",
        description: editPlanData?.description ?? "",
        alias_description: editPlanData?.alias_description ?? "",
        terms: editPlanData?.terms ?? "",
        alias_terms: editPlanData?.alias_terms ?? "",
        // Plans authored before the badge existed hold none, and open with an empty box.
        discount_text: editPlanData?.discount_text ?? "",
        alias_discount_text: editPlanData?.alias_discount_text ?? "",
        image: editPlanData?.image ?? "",
        alias_image: editPlanData?.alias_image ?? "",
        // Plans authored before the outlet was recorded have none, and those still open with
        // an empty picker and the saved item shown from the line itself.
        menu_customer_key: line?.menu_customer_key ?? "",
        main_product_id: line?.product_id ?? "",
        /**
         * Every size the tile carries, not just the row that was clicked - the sizes were chosen
         * together and are edited together. A plan that is not grouped opens on its own size,
         * which is the only one it has.
         */
        selected_options: editPlanData?.group_sizes?.length
          ? editPlanData.group_sizes.map((size) => size.variant_id)
          : [line?.variant_id || line?.product_id].filter(Boolean),
        quantity: line?.quantity ?? 1,
        pricing_mode: "discount",
        discount_percent: editPlanData?.discount_percent ?? "",
        /**
         * Each size reopens on the figures it was actually saved at, which on a group priced
         * size by size are not the clicked row's. Taking that row's for all of them would show
         * the team a group priced at one rate and flatten the rest onto it the next time they
         * saved.
         */
        discount_percents: (editPlanData?.group_sizes ?? []).reduce(
          (carry, size) => {
            if (size?.variant_id) {
              carry[size.variant_id] = size?.discount_percent ?? "";
            }

            return carry;
          },
          {}
        ),
        selling_prices: (editPlanData?.group_sizes ?? []).reduce(
          (carry, size) => {
            if (size?.variant_id) {
              carry[size.variant_id] = size?.selling_price ?? "";
            }

            return carry;
          },
          {}
        ),
        selling_price: editPlanData?.selling_price ?? "",
        validity_days: editPlanData?.validity_days ?? 30,
        daily_limit: editPlanData?.daily_limit ?? 1,
        purchase_limit: editPlanData?.purchase_limit ?? "",
        all_stores: editPlanData?.all_stores ?? true,
        stores: editPlanData?.stores ?? [],
        status: editPlanData?.status ?? 1,
        // Plans saved before the split hold no audience and are public, same as the server
        // reads them - so an edit that never touches the field re-saves what it already was.
        audience: editPlanData?.audience ?? 1,
        priority: editPlanData?.priority ?? 0,
        // Plans saved before grouping existed hold no flag and list flat, which is the same
        // reading the service takes of a missing value.
        group_by_product: Boolean(editPlanData?.group_by_product),
        // The size the group already opens on. Absent where the team never chose one, which
        // leaves the picker on "Dearest size" rather than pretending they had.
        default_variant: editPlanData?.group_default_variant ?? "",
      });

      dispatch(
        updateConfig((state) => {
          state.imagePreview = editPlanData?.image ?? "";
          state.imageName = editPlanData?.image?.split("/").pop() ?? "";
          state.aliasImagePreview = editPlanData?.alias_image ?? "";
          state.aliasImageName =
            editPlanData?.alias_image?.split("/").pop() ?? "";
          state.pricing = editPlanData?.pricing ?? {};
        })
      );
    }
    //eslint-disable-next-line
  }, [isEdit, editPlanData]);

  /**
   * Keeps the per-size discounts in step with the sizes that are ticked.
   *
   * A size newly ticked starts at whatever the single field held, so a group the team wants at
   * one rate is still one number typed once and only becomes several when they want several.
   * A size unticked is dropped rather than left behind: the service refuses a discount naming a
   * size the plan does not include, and the form should never put it in that position.
   *
   * Seeded on a change of sizes only. Watching the shared field as well would overwrite every
   * per-size figure the moment anybody touched it.
   */
  useEffect(() => {
    if (!isPerSizePricing) {
      return;
    }

    const sizes = selectedRows.map((row) => row?.variant_id).filter(Boolean);
    const current = formik.values[perSizeField] ?? {};
    const shared =
      perSizeField === "discount_percents"
        ? formik.values.discount_percent
        : formik.values.selling_price;

    const next = {};
    sizes.forEach((variantId) => {
      next[variantId] = current[variantId] ?? shared ?? "";
    });

    // Compared by content: the object is rebuilt on every render, so comparing identity would
    // leave this setting state forever.
    const unchanged =
      Object.keys(next).length === Object.keys(current).length &&
      sizes.every((variantId) => next[variantId] === current[variantId]);

    if (!unchanged) {
      formik.setFieldValue(perSizeField, next);
    }
    //eslint-disable-next-line
  }, [selectedKey, isPerSizePricing, perSizeField]);

  /**
   * Live pricing. The list prices come from the catalog, so nothing here can be worked out
   * locally - the service is the only thing that can say what a plan costs or what each
   * item will redeem at. Debounced because the team types a discount digit by digit.
   */
  useEffect(() => {
    const { quantity, pricing_mode, discount_percent, selling_price } =
      formik.values;

    const priced = isPerSizePricing
      ? // Every size has to carry a figure before the panel can price anything, or it would
        // quote the sizes already typed and leave the rest reading as free.
        selectedRows.every((row) => {
          const entry = formik.values[perSizeField]?.[row?.variant_id];

          return entry !== "" && entry !== null && entry !== undefined;
        })
        ? "ready"
        : ""
      : pricing_mode === "discount"
      ? discount_percent
      : selling_price;

    if (selectedRows.length === 0 || !quantity || priced === "" || priced === null) {
      return;
    }

    const timer = setTimeout(async () => {
      /**
       * One line per ticked size, so the panel can show what each will cost. These are priced
       * together only to save a round trip - they become separate plans, so the panel reads
       * the per-line figures and never the combined total, which would be the price of buying
       * every size at once and is a number nobody is ever charged.
       */
      const body = {
        lines: selectedRows.map((row, index) => ({
          type: "product",
          product_id: row?.product_id,
          variant_id: row?.variant_id || null,
          quantity: Number(quantity),
          line_id: `preview-${index}`,
        })),
      };
      if (isPerSizePricing) {
        /**
         * The sizes travel with the body so the service prices them as the separate plans they
         * will become - one figure each, each struck against that size's own list value, and the
         * one-product rule applied to each rather than across the set, which would refuse three
         * sizes for being three lines.
         */
        body.variants = selectedRows.map((row) => row?.variant_id).filter(Boolean);
        body[perSizeField] = selectedRows.reduce((carry, row) => {
          const variantId = row?.variant_id;

          if (variantId) {
            carry[variantId] = Number(
              formik.values[perSizeField]?.[variantId] ?? 0
            );
          }

          return carry;
        }, {});
      } else if (pricing_mode === "discount") {
        body.discount_percent = Number(discount_percent);
      } else {
        body.selling_price = Number(selling_price);
      }

      dispatch(
        updateConfig((state) => {
          state.isPricingLoading = true;
        })
      );

      const response = await getPlanPreview(body);

      dispatch(
        updateConfig((state) => {
          state.isPricingLoading = false;
          state.pricing = response?.data?.data ?? {};
        })
      );
    }, 500);

    return () => clearTimeout(timer);
    //eslint-disable-next-line
  }, [
    // The ticked sizes, not product_id: every size of one drink shares that id, so keying on
    // it would leave the panel quoting the size no longer selected.
    selectedKey,
    formik.values.quantity,
    formik.values.pricing_mode,
    formik.values.discount_percent,
    // Serialised rather than passed as the objects they are: a fresh object every render would
    // re-fire this on renders where no size was repriced.
    JSON.stringify(formik.values.discount_percents),
    JSON.stringify(formik.values.selling_prices),
    formik.values.selling_price,
  ]);

  const handleImageChange = async (event, field = "image") => {
    const selectedImage = event?.target?.files?.[0];
    if (!selectedImage) {
      return;
    }
    if (
      !["image/jpeg", "image/png", "image/jpg"].includes(selectedImage.type)
    ) {
      // Touched first, or the message sits in state unread - picking a file from the dialog
      // need never blur the input that opened it.
      formik.setFieldTouched(field, true, false);
      formik.setFieldError(field, "Only image files are allowed");
      return;
    }

    const isAlias = field === "alias_image";
    const formData = new FormData();
    formData.append("image", selectedImage);

    dispatch(
      updateConfig((state) => {
        if (isAlias) {
          state.isAliasImageUploading = true;
        } else {
          state.isImageUploading = true;
        }
      })
    );

    const response = await uploadPlanImage(formData);
    try {
      if (response?.data?.status_code === 200) {
        formik.setFieldValue(field, response?.data?.data?.image_path ?? "");
      } else if (
        response?.error?.data?.status_code === 422 &&
        response?.error?.data?.errors
      ) {
        Object.keys(response?.error?.data?.errors).forEach((key) => {
          formik.setFieldError(field, response?.error?.data?.errors[key]);
        });
      } else {
        toast.error(`Unable to add image, Please try again.`);
      }
    } catch (error) {
      console.error(`Unable to add image:`, error);
    } finally {
      dispatch(
        updateConfig((state) => {
          if (isAlias) {
            state.isAliasImageUploading = false;
          } else {
            state.isImageUploading = false;
          }
        })
      );
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      dispatch(
        updateConfig((state) => {
          if (isAlias) {
            state.aliasImagePreview = reader.result;
            state.aliasImageName = selectedImage?.name;
          } else {
            state.imagePreview = reader.result;
            state.imageName = selectedImage?.name;
          }
        })
      );
    };
    reader.readAsDataURL(selectedImage);
  };

  const handleAllStoresChange = (checked) => {
    formik.setFieldValue("all_stores", checked);
    if (checked) {
      formik.setFieldValue("stores", []);
    }
  };

  const handleMenuStoreChange = (value) => {
    setFieldValueTouched("menu_customer_key", value?._id ?? "");
    // The new outlet's menu may not carry the chosen item at all, so the selection is
    // dropped rather than left pointing at something this store does not sell. The sizes go
    // with it: outlets hold their own product documents, so the same drink's sizes carry
    // different ids from one store to the next.
    formik.setFieldValue("main_product_id", "");
    formik.setFieldValue("selected_options", []);
  };

  /**
   * Choosing a different drink drops the sizes with it - they are ids belonging to the drink
   * that was chosen before, and a Latte's Small means nothing on a Cappuccino.
   */
  const handleMainProductChange = (value) => {
    setFieldValueTouched("main_product_id", value?.product_id ?? "");
    setFieldValueTouched("selected_options", []);
  };

  /**
   * An edit is one plan, so the picker hands back a single option there and an array on create.
   * Normalised to an array either way, because that is what everything downstream reads.
   */
  const handleSizesChange = (value) => {
    const picked = Array.isArray(value) ? value : [value].filter(Boolean);
    const chosen = picked.map((row) => row?.option_id).filter(Boolean);

    // A size already on sale stays whatever the picker was just told - react-select offers a
    // cross on every chip and there is no way to hide only some of them, so the removal is
    // undone here instead.
    const kept = lockedSizes.filter((size) => !chosen.includes(size));

    setFieldValueTouched("selected_options", [...chosen, ...kept]);

    // Ticking a size on a form opened without a drink fills the drink in, so the picker above
    // stops reading as though nothing had been chosen.
    if (!formik.values.main_product_id && picked[0]?.product_id) {
      formik.setFieldValue("main_product_id", picked[0].product_id);
    }
  };

  const getFieldError = (fieldName) => {
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return formik.errors[fieldName];
    }
    return "";
  };

  const closeModal = () => {
    dispatch(
      updateConfig((state) => {
        state.showCreateModal = false;
        state.isEdit = false;
        state.pricing = {};
      })
    );
  };

  const menuStoreObj = useMemo(
    () => storeList?.filter((item) => item?._id === formik.values.menu_customer_key),
    [storeList, formik.values.menu_customer_key]
  );

  const storesObj = useMemo(
    () => storeList?.filter((item) => formik.values.stores?.includes(item?._id)),
    [storeList, formik.values.stores]
  );

  const statusObj = useMemo(
    () => planStatuses?.filter((item) => item?.id === Number(formik.values.status)),
    [planStatuses, formik.values.status]
  );

  const audienceObj = useMemo(
    () =>
      planAudiences?.filter((item) => item?.id === Number(formik.values.audience)),
    [planAudiences, formik.values.audience]
  );

  /** The drink row the picker highlights, from the saved line when the menu cannot answer. */
  const mainProductObj = useMemo(() => {
    const chosen = formik.values.main_product_id;

    if (!chosen) {
      return [];
    }

    const fromMenu = mainProductList?.filter((row) => row?.product_id === chosen);

    return fromMenu?.length > 0
      ? fromMenu
      : [{ product_id: chosen, name: selectedRows[0]?.name ?? "Saved beverage" }];
  }, [mainProductList, formik.values.main_product_id, selectedRows]);

  /**
   * What the team is about to create, named the way the service will name it - their own plan
   * name with each size appended.
   *
   * Shown rather than left to be discovered after saving: one box producing three differently
   * named plans is worth seeing before it happens, and it mirrors sizedName() on the service
   * exactly, including what it does with a size that has no name.
   */
  const mintedNames = useMemo(() => {
    if (selectedRows.length < 2) {
      return [];
    }

    const planName = (formik.values.name ?? "").trim();

    return selectedRows.map((row) => {
      const size = (row?.variant_name ?? "").trim();

      if (!planName || !size) {
        return planName || size;
      }

      return `${planName} - ${size}`;
    });
  }, [selectedRows, formik.values.name]);

  return {
    formik,
    isEdit,
    pricing,
    sizeList,
    storeList,
    statusObj,
    storesObj,
    imageName,
    audienceObj,
    productList,
    mintedNames,
    planStatuses,
    planAudiences,
    menuStoreObj,
    isPlanSaving,
    imagePreview,
    selectedRows,
    lockedSizes,
    isPerSizePricing,
    perSizeField,
    aliasImageName,
    mainProductObj,
    mainProductList,
    isPricingLoading,
    isImageUploading,
    aliasImagePreview,
    isProductsFetching,
    isAliasImageUploading,
    activeSubscriptionCount: editPlanData?.active_subscription_count,
    fieldProps,
    setFieldValueTouched,
    handleSizesChange,
    handleMenuStoreChange,
    handleAllStoresChange,
    handleMainProductChange,
    handleImageChange,
    getFieldError,
    closeModal,
  };
};

export default useAddSubscriptionPlan;
